import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ComingSoonModal from './ComingSoonModal';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isNavbarHidden, setIsNavbarHidden] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const servicesLinkRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);

  useEffect(() => {
    let lastScrollTop = 0;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Hide/show navbar on scroll
      if (scrollTop > lastScrollTop && scrollTop > 300) {
        setIsNavbarHidden(true);
      } else if (scrollTop <= 300) {
        setIsNavbarHidden(false);
      }
      
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

      // Update active section
      updateActiveSection();
    };

    const updateActiveSection = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const scrollPosition = scrollTop + (windowHeight * 0.3);

      const sections = ['home', 'about', 'services', 'pricing', 'team', 'contact'];
      let currentSection = 'home';

      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element && scrollPosition >= element.offsetTop + 100) {
          currentSection = section;
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    updateActiveSection();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update dropdown position and close when clicking outside (for mobile/touch devices)
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (isServicesDropdownOpen && servicesLinkRef.current && dropdownRef.current) {
        const rect = servicesLinkRef.current.getBoundingClientRect();
        dropdownRef.current.style.top = `${rect.bottom + 4}px`;
        dropdownRef.current.style.left = `${rect.left}px`;
        dropdownRef.current.style.position = 'fixed';
        dropdownRef.current.style.zIndex = '10003';
        dropdownRef.current.style.display = 'block';
      }
    };

    if (isServicesDropdownOpen) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        updateDropdownPosition();
      }, 0);
      window.addEventListener('scroll', updateDropdownPosition);
      window.addEventListener('resize', updateDropdownPosition);
    }

    const handleClickOutside = (e) => {
      if (isServicesDropdownOpen && !e.target.closest('.nav-dropdown')) {
        setIsServicesDropdownOpen(false);
      }
    };

    // Only add click handler on mobile/touch devices (where hover doesn't work)
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isServicesDropdownOpen && isTouchDevice) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition);
      window.removeEventListener('resize', updateDropdownPosition);
      if (isTouchDevice) {
        document.removeEventListener('click', handleClickOutside);
      }
      // Clean up timeout on unmount
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
        dropdownTimeoutRef.current = null;
      }
    };
  }, [isServicesDropdownOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    const section = document.getElementById(sectionId);
    if (section) {
      const navbar = document.querySelector('[data-navbar]');
      const navbarHeight = navbar ? navbar.offsetHeight : 140;
      const targetPosition = section.offsetTop - navbarHeight;
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
    closeMenu();
    setIsServicesDropdownOpen(false);
  };

  const handleServiceClick = (e, serviceId) => {
    e.preventDefault();
    const section = document.getElementById('services');
    if (section) {
      const navbar = document.querySelector('[data-navbar]');
      const navbarHeight = navbar ? navbar.offsetHeight : 140;
      const targetPosition = section.offsetTop - navbarHeight;
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
      
      // Wait for scroll to complete, then scroll to specific service
      setTimeout(() => {
        const serviceElement = document.getElementById(serviceId);
        if (serviceElement) {
          const servicePosition = serviceElement.offsetTop - navbarHeight;
          window.scrollTo({
            top: servicePosition,
            behavior: 'smooth'
          });
        }
      }, 500);
    }
    setIsServicesDropdownOpen(false);
    closeMenu();
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const handleSignupClick = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  return (
    <>
    <nav
      data-navbar
      className={`fixed top-0 left-0 right-0 w-full max-w-[100vw] bg-[#1a1a1a] backdrop-blur-[10px] z-[1000] transition-transform duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] py-3 m-0 overflow-x-hidden overflow-y-visible ${
        isNavbarHidden ? '-translate-y-full' : ''
      }`}
    >
      {/* Background grain texture */}
      <div
        className="absolute top-0 left-0 w-full h-full opacity-[0.025] pointer-events-none z-0 overflow-hidden"
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 255, 255, 0.08) 2px, rgba(255, 255, 255, 0.08) 4px),
            repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255, 255, 255, 0.08) 2px, rgba(255, 255, 255, 0.08) 4px)
          `,
          backgroundSize: '120px 120px'
        }}
      />

      <div className="max-w-[1600px] w-full mx-auto px-4 sm:px-6 md:px-8 lg:px-12 relative z-[1] overflow-visible box-border">
        {/* Hamburger menu - hidden on desktop */}
        <button 
          className={`hidden md:hidden flex-col justify-around w-[30px] h-[30px] bg-transparent border-0 cursor-pointer p-0 z-[1002] absolute left-4 ${
            isMenuOpen ? 'active' : ''
          }`}
          id="hamburgerMenu"
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <span className="w-full h-[3px] bg-white rounded-[3px] transition-all duration-300 origin-center"></span>
          <span className="w-full h-[3px] bg-white rounded-[3px] transition-all duration-300 origin-center"></span>
          <span className="w-full h-[3px] bg-white rounded-[3px] transition-all duration-300 origin-center"></span>
        </button>

        {/* Main container with company name and nav/auth */}
        <div className="flex items-center justify-between w-full min-h-[40px]">
          {/* Company name/Logo */}
          <div className="text-white text-sm sm:text-base md:text-lg lg:text-xl font-semibold tracking-[1px] sm:tracking-[2px] md:tracking-[3px] uppercase font-serif whitespace-nowrap leading-[1.5] flex-shrink-0 relative pb-1 mr-4 sm:mr-6 md:mr-8 lg:mr-10 after:content-[''] after:absolute after:-bottom-[2px] after:left-0 after:w-[45px] sm:after:w-[60px] md:after:w-[75px] after:h-[2px] sm:after:h-[3px] after:bg-[var(--accent-color)]">
            The VBC Group
          </div>

          {/* Nav menu and auth buttons container */}
          <div className="flex items-center justify-end gap-2 sm:gap-3 md:gap-4 lg:gap-6 flex-shrink min-w-0 ml-6 sm:ml-8 md:ml-10 lg:ml-12">
            {/* Navigation menu */}
            <ul
              className={`flex list-none gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 xl:gap-6 justify-end flex-nowrap m-0 p-0 overflow-visible ${
                isMenuOpen ? 'active' : ''
              }`}
              id="navMenu"
            >
          <li>
            <a
              href="#home"
              className={`relative text-white no-underline font-normal text-[0.65rem] sm:text-xs md:text-sm uppercase tracking-[0.5px] sm:tracking-[1px] md:tracking-[1.5px] transition-colors duration-300 whitespace-nowrap ${
                activeSection === 'home' ? 'text-[#9a83ad]' : ''
              }`}
              onClick={(e) => handleNavClick(e, 'home')}
            >
              Home
            </a>
          </li>
          <li>
            <a
              href="#about"
              className={`relative text-white no-underline font-normal text-[0.65rem] sm:text-xs md:text-sm uppercase tracking-[0.5px] sm:tracking-[1px] md:tracking-[1.5px] transition-colors duration-300 whitespace-nowrap ${
                activeSection === 'about' ? 'text-[#9a83ad]' : ''
              }`}
              onClick={(e) => handleNavClick(e, 'about')}
            >
              About us
            </a>
          </li>
          <li 
            className={`nav-dropdown relative ${isServicesDropdownOpen ? 'active' : ''}`}
            style={{ zIndex: 10001 }}
            onMouseEnter={() => {
              // Clear any pending close timeout
              if (dropdownTimeoutRef.current) {
                clearTimeout(dropdownTimeoutRef.current);
                dropdownTimeoutRef.current = null;
              }
              setIsServicesDropdownOpen(true);
            }}
            onMouseLeave={() => {
              // Add a small delay before closing to allow moving to dropdown
              dropdownTimeoutRef.current = setTimeout(() => {
                setIsServicesDropdownOpen(false);
              }, 150);
            }}
          >
            <a
              ref={servicesLinkRef}
              href="#services"
              className={`dropdown-toggle relative text-white no-underline font-normal text-[0.65rem] sm:text-xs md:text-sm uppercase tracking-[0.5px] sm:tracking-[1px] md:tracking-[1.5px] transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                activeSection === 'services' ? 'text-[#9a83ad]' : ''
              }`}
              onClick={(e) => handleNavClick(e, 'services')}
            >
              Services
            </a>
            {isServicesDropdownOpen && createPortal(
              <ul 
                ref={dropdownRef}
                className="fixed z-[10003] opacity-100 visible block translate-y-0 max-h-[500px] h-auto py-2 pointer-events-auto bg-[#2a2a2a] list-none m-0 mt-2 min-w-[250px] shadow-lg border border-[#333] transition-all duration-300 ease-in-out overflow-visible"
                onMouseEnter={() => {
                  // Clear any pending close timeout when entering dropdown
                  if (dropdownTimeoutRef.current) {
                    clearTimeout(dropdownTimeoutRef.current);
                    dropdownTimeoutRef.current = null;
                  }
                  setIsServicesDropdownOpen(true);
                }}
                onMouseLeave={() => {
                  // Close when leaving dropdown
                  setIsServicesDropdownOpen(false);
                }}
              >
              <li className="m-0 p-0">
                <a
                  href="#service-p2p"
                  onClick={(e) => handleServiceClick(e, 'service-p2p')}
                  className="block py-3 px-6 pl-10 text-white no-underline text-sm uppercase tracking-[1.5px] transition-all duration-300 border-b border-white/5 hover:bg-white/15 hover:text-[#9a83ad] hover:pl-12"
                >
                  Peer to Peer Texting
                </a>
              </li>
              <li className="m-0 p-0">
                <a
                  href="#service-auto-call"
                  onClick={(e) => handleServiceClick(e, 'service-auto-call')}
                  className="block py-3 px-6 pl-10 text-white no-underline text-sm uppercase tracking-[1.5px] transition-all duration-300 border-b border-white/5 hover:bg-white/15 hover:text-[#9a83ad] hover:pl-12"
                >
                  Automated Phone Calls
                </a>
              </li>
              <li className="m-0 p-0">
                <a
                  href="#service-survey"
                  onClick={(e) => handleServiceClick(e, 'service-survey')}
                  className="block py-3 px-6 pl-10 text-white no-underline text-sm uppercase tracking-[1.5px] transition-all duration-300 border-b border-white/5 hover:bg-white/15 hover:text-[#9a83ad] hover:pl-12"
                >
                  Surveys and Polling
                </a>
              </li>
              <li className="m-0 p-0">
                <a
                  href="#service-agent"
                  onClick={(e) => handleServiceClick(e, 'service-agent')}
                  className="block py-3 px-6 pl-10 text-white no-underline text-sm uppercase tracking-[1.5px] transition-all duration-300 border-b border-white/5 hover:bg-white/15 hover:text-[#9a83ad] hover:pl-12"
                >
                  Live Agent Calls
                </a>
              </li>
              <li className="m-0 p-0">
                <a
                  href="#service-tele-townhall"
                  onClick={(e) => handleServiceClick(e, 'service-tele-townhall')}
                  className="block py-3 px-6 pl-10 text-white no-underline text-sm uppercase tracking-[1.5px] transition-all duration-300 border-b border-white/5 hover:bg-white/15 hover:text-[#9a83ad] hover:pl-12"
                >
                  Tele-Townhalls
                </a>
              </li>
              <li className="m-0 p-0">
                <a
                  href="#service-data"
                  onClick={(e) => handleServiceClick(e, 'service-data')}
                  className="block py-3 px-6 pl-10 text-white no-underline text-sm uppercase tracking-[1.5px] transition-all duration-300 border-b-0 hover:bg-white/15 hover:text-[#9a83ad] hover:pl-12"
                >
                  Registered Voter, Donor and Consumer Data
                </a>
              </li>
              </ul>,
              document.body
            )}
          </li>
          <li>
            <a
              href="#pricing"
              className={`relative text-white no-underline font-normal text-[0.65rem] sm:text-xs md:text-sm uppercase tracking-[0.5px] sm:tracking-[1px] md:tracking-[1.5px] transition-colors duration-300 whitespace-nowrap ${
                activeSection === 'pricing' ? 'text-[#9a83ad]' : ''
              }`}
              onClick={(e) => handleNavClick(e, 'pricing')}
            >
              Pricing
            </a>
          </li>
          <li>
            <a
              href="#team"
              className={`relative text-white no-underline font-normal text-[0.65rem] sm:text-xs md:text-sm uppercase tracking-[0.5px] sm:tracking-[1px] md:tracking-[1.5px] transition-colors duration-300 whitespace-nowrap ${
                activeSection === 'team' ? 'text-[#9a83ad]' : ''
              }`}
              onClick={(e) => handleNavClick(e, 'team')}
            >
              Meet Our Team
            </a>
          </li>
          <li>
            <a
              href="#contact"
              className={`relative text-white no-underline font-normal text-[0.65rem] sm:text-xs md:text-sm uppercase tracking-[0.5px] sm:tracking-[1px] md:tracking-[1.5px] transition-colors duration-300 whitespace-nowrap ${
                activeSection === 'contact' ? 'text-[#9a83ad]' : ''
              }`}
              onClick={(e) => handleNavClick(e, 'contact')}
            >
              Contact Us
            </a>
          </li>
          </ul>

          {/* Auth buttons */}
          <div className={`hidden md:flex gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 items-center flex-shrink-0 ml-2 sm:ml-3 md:ml-4 ${isMenuOpen ? 'active' : ''}`}>
            <a
              href="#login"
              className="no-underline font-normal text-[0.65rem] sm:text-xs md:text-sm uppercase tracking-[0.5px] sm:tracking-[1px] md:tracking-[1.5px] transition-all duration-300 whitespace-nowrap py-1.5 px-3 md:py-2 md:px-4 lg:py-2.5 lg:px-6 rounded border border-white text-white bg-transparent hover:text-[#9a83ad] hover:border-[#9a83ad] hover:bg-[rgba(154,131,173,0.05)] font-serif"
              id="loginBtn"
              onClick={handleLoginClick}
            >
              Login
            </a>
            <a
              href="#signup"
              className="no-underline font-normal text-[0.65rem] sm:text-xs md:text-sm uppercase tracking-[0.5px] sm:tracking-[1px] md:tracking-[1.5px] transition-all duration-300 whitespace-nowrap py-1.5 px-3 md:py-2 md:px-4 lg:py-2.5 lg:px-6 rounded bg-[#9a83ad] border border-[#9a83ad] text-white hover:bg-[#dfc0b5] hover:border-[#dfc0b5] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(154,131,173,0.3)] font-serif"
              id="signupBtn"
              onClick={handleSignupClick}
            >
              Sign Up
            </a>
          </div>
          </div>
        </div>
      </div>
    </nav>
    <ComingSoonModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default Navbar;
